// @ts-strict-ignore
/**
 * This module serves as "interceptor" or "middleware" before key workflows reports
 */
import omit from 'lodash/omit';
import omitBy from 'lodash/omitBy';
import isNil from 'lodash/isNil';
import type {Location} from 'react-router';

import {ChartType, InsightsBookmarkParams, MetricType} from 'common/types/reports/bookmark';

import type {BookmarkURLParams} from '../queries/app-api/bookmark-urls-client';
import {getBehaviorShowClauses} from '../insights/models/utils';
import {BookmarkType} from '../queries/app-api/bookmark-client';
import {serializeBookmark} from '../insights/contexts/utils/bookmark';

// Reduced segmentation limits for all insights charts for certain projects
const REDUCED_LIMIT_PROJECT_IDS = [`2928584`];

/**
 * Runs before making query to query-api
 *
 * It could dynamically transform current query payload and add special handlings if needed
 *
 * For now our serialized version of bookmarkParams is still in old format
 * So we do transformation from old -> new
 *
 * Once we convert the bookmarkParams shape to new format
 * we have to do transformation from new -> old depending on feature flag
 */
export function beforeQuery({
  endpoint,
  urlParams,
  body,
  location,
}: {
  // insights / funnels / flows / retention
  endpoint: string;
  // includes project_id etc.
  urlParams: URLSearchParams;
  // request payload for qapi
  body: TODO;
  location: Location;
}) {
  let transformedBody = body;
  // Temporary fix for CrunchyRoll + OneFootball
  // TODO jayantj: remove once merger optimization is shipped
  transformedBody = addLimitsToSlowProjects({urlParams, endpoint, body: transformedBody});

  transformedBody = addForecastToDisplayOptions({endpoint, body: transformedBody, location});
  transformedBody = transformInsightsSingleReportPayload({endpoint, body: transformedBody});

  return {
    endpoint,
    urlParams,
    body: transformedBody,
  };
}

/**
 * Hook for dynamically transform cleaner url params
 *
 * For now, we just remove the extra field `behaviorType` on show clauses
 */
export function beforeCleanerUrl(params: BookmarkURLParams) {
  return omitBy(omit(params, [`project_id`]), (value) => isNil(value));
}

/**
 * Hook to run before saving bookmarks to the webapp
 * @todo(metrics) marked for deletion
 */
export function beforeSavingBookmark({params, type}: {params: unknown; type: BookmarkType}) {
  return serializeBookmark({type, params, name: ``}).params;
}

//==================================================================================================
// The rest are private pure functions to help with the above 2 hooks
// can spread out those functions in different files if needed
//==================================================================================================
function transformFunnels({bookmarkParams}: {bookmarkParams: InsightsBookmarkParams}) {
  return {
    ...bookmarkParams,
    displayOptions: {
      ...bookmarkParams.displayOptions,
      funnelStepsSelectedTableColumns: {
        'conv-first-step': false,
        'conv-prev-step': true,
        count: true,
        'stat-sig': true,
        'time-first-step': false,
        'time-prev-step': true,
      },
    },
  };
}

// TODO make this function to work when there are different types of behavior metrics in the show section
function transformBookmarkParams({bookmarkParams}: {bookmarkParams: InsightsBookmarkParams}) {
  const showSections = bookmarkParams.sections.show;
  if (getBehaviorShowClauses(showSections).some((showClause) => showClause.behavior.type === MetricType.Funnel)) {
    return transformFunnels({bookmarkParams});
  }

  return bookmarkParams;
}

function transformInsightsSingleReportPayload({endpoint, body}: {endpoint: string; body: any}): InsightsBookmarkParams {
  if (endpoint !== `insights` && endpoint !== `insights/query_metadata`) return body;
  const bookmarkParams = body.bookmark as InsightsBookmarkParams;

  if (bookmarkParams?.sections.show) {
    bookmarkParams.sections.show = bookmarkParams.sections.show.map((clause) => omit(clause, `userNamed`, `_idx`));
  }

  return {
    ...body,
    bookmark: transformBookmarkParams({bookmarkParams}),
  };
}

// implements a query param flag for forcing forecast queries, intended only for debugging purposes
function addForecastToDisplayOptions({endpoint, body, location}: {endpoint: string; body: TODO; location: Location}) {
  if (endpoint !== `insights`) return body;
  const bookmarkParams = body.bookmark as InsightsBookmarkParams;
  const searchParams = new URLSearchParams(location.search ?? ``);
  if (
    [`1`, `true`].includes(searchParams.get(`force_forecast`)) &&
    bookmarkParams.displayOptions?.chartType === ChartType.Line
  ) {
    return {
      ...body,
      queryLimits: {limit: parseFloat(searchParams.get(`query_limit`) || `20`)},
      bookmark: {
        ...bookmarkParams,
        forecastComparison: {
          trainingMode: searchParams.get(`training`) || `agile`,
          confidence: parseFloat(searchParams.get(`confidence`) || `0.95`),
        },
      },
    };
  }
  return body;
}

function addLimitsToSlowProjects({
  urlParams,
  endpoint,
  body,
}: {
  urlParams: URLSearchParams;
  endpoint: string;
  body: Obj<any>;
}) {
  if (endpoint !== `insights`) return body;
  if (!body.queryLimits) return body;
  let limit = body.queryLimits?.limit;

  if (REDUCED_LIMIT_PROJECT_IDS.includes(urlParams.get(`project_id`)) && body?.format !== `csv`) {
    limit = 300;
  }
  return {
    ...body,
    queryLimits: {
      ...body.queryLimits,
      limit,
    },
  };
}
